import React, { Component } from 'react';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import styles from './Legal.module.scss';

class PrivacyPolicy extends Component {
  render() {
    return (
      <div className={styles.legalPage}>
        <DashboardHeader />
        <div className={styles.legalContainer}>
          <article className={styles.legalCard}>
            <h1 className={styles.pageTitle}>Privacy Policy</h1>
            <p className={styles.lastUpdated}>Last updated: 31 July 2026</p>

            <p className={styles.intro}>
              This Privacy Policy explains how <span className={styles.entityName}>Chatup24 Global
              Technologies L.L.C-FZ</span> ("Chatup24", "we", "us" or "our") collects, uses,
              shares and protects your personal data when you use the Chatup24 platform at
              chatup24.com and our related mobile applications (together, the "Service"). We are
              committed to handling your personal data in accordance with the UAE Personal Data
              Protection Law — Federal Decree-Law No. 45 of 2021 (the "PDPL") and other applicable
              laws of the United Arab Emirates.
            </p>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>1. Who We Are</h2>
              <p className={styles.paragraph}>
                Chatup24 Global Technologies L.L.C-FZ is a free-zone company registered in the
                Meydan Free Zone, Dubai, United Arab Emirates (Trade License No. 2651661.01). We
                operate Chatup24, a platform that connects customers with venues such as bars,
                restaurants, salons, hotels and grocery stores so you can call staff, place orders,
                chat, make bookings and pay. For the purposes of the PDPL, we act as the data
                controller for the personal data described in this policy.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>2. Personal Data We Collect</h2>
              <p className={styles.paragraph}>We collect the following categories of personal data:</p>
              <h3 className={styles.subTitle}>Account information</h3>
              <ul className={styles.list}>
                <li>Your name, email address and/or mobile phone number, which we may receive when
                  you sign in using Google login or phone (OTP) login.</li>
                <li>A profile photo or display name, where you choose to provide one.</li>
              </ul>
              <h3 className={styles.subTitle}>Usage and activity data</h3>
              <ul className={styles.list}>
                <li>Bookings, orders, call-staff requests and reservations you make through the
                  Service.</li>
                <li>Chat messages and requests you exchange with a venue's staff.</li>
                <li>Device and session information, including QR-code table or room sessions, app
                  and browser type, and approximate technical identifiers.</li>
              </ul>
              <h3 className={styles.subTitle}>Payment data</h3>
              <ul className={styles.list}>
                <li>The status of your payments (for example, whether an order was paid online or is
                  to be settled at the venue).</li>
                <li>Card and online payments are processed by our third-party payment gateway. We do
                  not collect or store your full card number or card security details.</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>3. Why We Use Your Data and Our Legal Basis</h2>
              <p className={styles.paragraph}>
                We process your personal data to provide and operate the Service, and rely on one or
                more of the legal bases recognised under the PDPL, including your consent, the
                performance of a contract with you, our legitimate interests, and compliance with
                legal obligations. Specifically, we use your data to:
              </p>
              <ul className={styles.list}>
                <li>Create and manage your account and authenticate your sign-in.</li>
                <li>Process your bookings, orders, call-staff requests and payments.</li>
                <li>Enable chat and communication between you and the venues you interact with.</li>
                <li>Send you service messages and, where enabled, push notifications about your
                  activity.</li>
                <li>Maintain the security, integrity and reliability of the Service.</li>
                <li>Comply with our legal and regulatory obligations.</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>4. How We Share Your Data</h2>
              <p className={styles.paragraph}>We share personal data only as needed to run the Service:</p>
              <ul className={styles.list}>
                <li><strong>Venues and organizers:</strong> when you place an order, make a booking,
                  send a call-staff request or chat, the relevant venue (organizer) receives the
                  information necessary to serve you.</li>
                <li><strong>Service providers:</strong> trusted providers who host our systems,
                  deliver notifications and support our operations, acting on our instructions.</li>
                <li><strong>Payment processors:</strong> our payment gateway partners, who process
                  online card payments securely.</li>
                <li><strong>Legal and regulatory authorities:</strong> where required by applicable
                  UAE law or a lawful request.</li>
              </ul>
              <p className={styles.paragraph}>We do not sell your personal data.</p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>5. Data Retention</h2>
              <p className={styles.paragraph}>
                We keep your personal data only for as long as necessary for the purposes described
                in this policy or as required by law. Where you use the Service as a guest via a
                table or room QR code, guest session data is anonymised or purged after the session
                ends (typically within 24 hours), except where we are required to retain records for
                legal or accounting purposes.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>6. Your Rights Under the PDPL</h2>
              <p className={styles.paragraph}>
                Subject to the conditions of Federal Decree-Law No. 45 of 2021, you have the right to:
              </p>
              <ul className={styles.list}>
                <li>Access the personal data we hold about you.</li>
                <li>Request correction of inaccurate or incomplete data.</li>
                <li>Request deletion of your data.</li>
                <li>Object to or restrict certain processing.</li>
                <li>Withdraw your consent at any time, without affecting processing carried out
                  before withdrawal.</li>
              </ul>
              <p className={styles.paragraph}>
                To exercise any of these rights, please contact us using the details below. We may
                need to verify your identity before responding.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>7. International Transfers</h2>
              <p className={styles.paragraph}>
                Some of our service providers may process data outside the United Arab Emirates.
                Where personal data is transferred internationally, we take appropriate safeguards so
                that it continues to be protected in a manner consistent with the PDPL.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>8. Security</h2>
              <p className={styles.paragraph}>
                We apply appropriate technical and organisational measures to protect your personal
                data against unauthorised access, loss or misuse. No method of transmission or
                storage is completely secure, but we work to safeguard your information and to
                address any incident promptly.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>9. Children</h2>
              <p className={styles.paragraph}>
                The Service is intended for users aged 18 and over. We do not knowingly collect
                personal data from anyone under 18. If you believe a minor has provided us with
                personal data, please contact us so we can take appropriate action.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>10. Changes to This Policy</h2>
              <p className={styles.paragraph}>
                We may update this Privacy Policy from time to time. When we do, we will revise the
                "Last updated" date above and, where appropriate, notify you through the Service.
              </p>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>11. Contact Us</h2>
              <p className={styles.paragraph}>
                If you have questions about this policy or wish to exercise your rights, contact us at:
              </p>
              <div className={styles.contactBlock}>
                <p className={styles.entityName}>Chatup24 Global Technologies L.L.C-FZ</p>
                <p>Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.</p>
                <p>Email: <a className={styles.link} href="mailto:privacy@chatup24.com">privacy@chatup24.com</a></p>
              </div>
            </section>
          </article>
        </div>
        <Footer />
      </div>
    );
  }
}

export default PrivacyPolicy;
